import styled, { css } from 'styled-components';
import { darkSurface } from '@/styles/theme';

export const StyledHeader = styled.header<{
  $sticky: boolean;
  $transparent: boolean;
  $isScrolled: boolean;
  $overDark: boolean;
}>`
  position: ${({ $sticky }) => ($sticky ? 'fixed' : 'relative')};
  top: 0;
  left: 0;
  right: 0;
  z-index: var(--z-sticky);
  transition: background-color var(--transition-normal), backdrop-filter var(--transition-normal);

  /* Dark treatment, three states in priority order.

     Over a dark region: black, no border. Not transparent, because a solid colour does not depend on
     the band still being exactly aligned with the header, so a one-pixel rounding difference cannot
     put white behind a black header. The band is the same #000, so there is no seam.

     The three declarations below are this element's own: they are layout, and they stay here. Every
     colour in the dark treatment is the shared darkSurface fragment, which is where their values and
     the contrast arithmetic behind them now live. Everything inside inherits by custom property rather
     than by a prop. Every colour in this subtree was already a var(--color-*), so overriding them
     cascades to the brand, the navigation and the menu button without threading anything through
     Navigation's API. Measured against #000, the stock values all fail: logo #1D1D1F is 1.25:1,
     navigation #6E6E73 is 4.14:1 and the GitHub CTA #0062C4 is 3.54:1.

     specs/022 is why --color-accent is one of the overrides rather than the underline variable it
     replaced, and why it needs two values instead of one. That reasoning moved to
     src/styles/theme.ts with the values it explains; nothing about it changed.

     The mobile menu is portaled to document.body, outside this subtree, so none of this reaches it.
     specs/026 gives it its own copy of the fragment. */
  ${({ $overDark }) =>
    $overDark &&
    css`
      background-color: #000000;
      backdrop-filter: none;
      border-bottom: none;

      ${darkSurface}
    `}

  ${({ $transparent, $isScrolled, $overDark }) =>
    !$overDark &&
    ($transparent && !$isScrolled
      ? css`
          background-color: transparent;
          backdrop-filter: none;
        `
      : css`
          background-color: var(--color-bg);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid var(--color-border);
        `)}
`;

export const StyledInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  /* From the token, so the Hero band can reserve the same height and the two cannot drift apart. */
  height: var(--header-height);
  padding-inline: var(--space-6);
  max-width: var(--container-xl);
  margin-inline: auto;
`;

export const StyledBrand = styled.div`
  display: flex;
  align-items: center;
`;

export const StyledLogo = styled.a`
  font-size: var(--text-label);
  font-weight: 500;
  letter-spacing: 0.02em;
  color: var(--color-fg);
  text-decoration: none;
  transition: color var(--transition-fast);

  &:hover {
    color: var(--color-fg-muted);
  }
`;

export const StyledNavWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-8);

  @media (max-width: 1023px) {
    gap: var(--space-4);
  }
`;

export const StyledCtaWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  font-size: var(--text-label);

  a {
    color: var(--color-accent-text);
    text-decoration: none;
    transition: color var(--transition-fast), transform var(--transition-fast);
    will-change: transform;

    &:hover {
      color: var(--color-accent-text-hover);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0) scale(0.97);
    }
  }
`;

export const StyledMenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: none;
  background: transparent;
  color: var(--color-fg);
  border-radius: var(--radius-full);
  cursor: pointer;
  transition: background-color var(--transition-fast), color var(--transition-fast);

  &:hover {
    background-color: var(--color-bg-muted);
  }

  &:active {
    background-color: var(--color-border);
  }

  &:focus-visible {
    outline: 2px solid var(--color-accent);
    outline-offset: 2px;
  }
`;
